// Error code translations used by internal apps (admin / worker / facility).
//
// Audience is staff, so it's fine to surface technical or data-integrity
// details here — they help operators diagnose issues. Be more conservative
// in the customer app (`web/noddi-customer/.../getErrorCodesWithTranslations.ts`),
// which masks internal failures and falls back to the generic error message
// for anything the end customer can't act on.
//
// A mapping here REPLACES the backend's own message (see
// `getInternalErrorMessageNormalized`), so only add a code whose backend copy is
// generic. When the backend names the specific obstacle — which shift overlaps,
// which payroll run to reopen, when the hours were paid — leave the code out so
// that detail reaches the user instead of being flattened to static text.
export const errorCodesEnglish = {
  not_authenticated: 'Your session has expired. Try refreshing the page, or sign in again in a new browser tab',
  authentication_failed: 'Your session has expired. Try refreshing the page, or sign in again in a new browser tab',
  // The backend names the missing request field, which no worker can act on.
  // Until the scan flow can ask for a storage unit, say what actually happened.
  route_item_cargo_scan_storage_unit_required_for_anomaly:
    'These wheels are not on this reload list, so they cannot be filed into storage from here. Contact the office to have them added.',
  invalid_phone_number: 'Phone number is not valid',
  invalid_credentials:
    'No user with the provided credentials was found - contact support if you think this is a mistake',
  invalid_verification_code: 'The verification code is not valid',
  password_same_as_current: 'Your new password must be different from your current one.',
  password_too_weak: 'Choose a stronger password. Avoid common words, simple number series and your name.',
  verification_code_expired: 'That code has expired. Ask for a new one.',
  verification_code_required: 'Enter the verification code we sent you.',
  verification_code_send_failed: "We couldn't send the verification code. Please try again.",
  invalid_code: 'The code is not valid',
  account_selection_expired: 'You took a bit too long to choose an account. Ask for a new code and try again.',
  account_selection_invalid: "We couldn't sign you in to that account. Ask for a new code and try again.",
  signup_reconciliation_expired: 'Your phone verification has expired. Verify your number again to continue.',
  signup_reconciliation_invalid: 'Your phone verification is no longer valid. Verify your number again to continue.',
  user_not_naf_member:
    'We could not find you in the NAF registers. You have to be a member to activate the benefits. If this is wrong, please contact support',
  referral_ineligible:
    'Referral discounts are available before your first completed booking, if you have not received a referral benefit before.',
  referral_cannot_activate_own: 'You cannot activate your own referral. Share this link with friends instead.',
  booking_time_window_not_bookable:
    'The chosen time slot has unfortunately been taken. Please choose another time slot in the previous step.',
  user_group_address_already_exists: 'This address is already added to the customer.',
  user_phone_or_email_already_exists:
    'It looks like there already exists a user with the given email. Contact support if you are unable to log into your account',
  car_not_found: 'Could not find the vehicle that was searched for',
  car_not_found_for_license_plate: 'Could not find the vehicle that was searched for',
  car_not_found_in_registry: 'Could not find the vehicle that was searched for',
  phone_number_verification_error: 'The phone number you provided is not valid',
  genericErrorMessage: 'Obs.. Something went wrong',
  car_not_found_car_info: `We couldn't find the car you searched for in the national vehicle registry`,
  vehicle_type_not_supported: `This vehicle type is not supported`,
  payroll_has_no_settlements: `The payroll has no route or time settlements for this employment type and period`,
  payroll_has_no_route_settlements: `The payroll has no route settlements for this employment type and period`,
  time_log_blocked_by_active_route: `You're on an active route. Finish the route before clocking in for other work`,
  time_log_already_open: `You're already clocked in. Clock out before starting a new timer`,
  time_log_invalid_interval: `The clock-out time has to be after the clock-in time`,
  // Deliberately unmapped — the backend names the specific obstacle and that is
  // more useful than static copy: time_settlement_paid (the payment date),
  // time_settlement_on_approved_payroll (the run to set back to draft),
  // time_log_overlap (the clashing shift), time_log_affects_decided_overtime
  // (the same-week shift whose decision would move).
  overtime_acknowledgment_required: `This whole shift will count as overtime — confirm and add a reason to clock in`,
  overtime_reason_required_for_approval: `Add the overtime reason before approving it`,
  overtime_entry_invalid_transition: `This overtime request can no longer be changed`,
  overtime_config_name_taken: `An overtime configuration with this name already exists — pick a different name`,
  overtime_config_in_use: `This overtime configuration is still assigned to one or more departments. Reassign them before deleting it`,
  car_not_found_statens_vegvesen: 'Could not find the vehicle that was searched for',
  statens_vegvesen_vehicle_not_found: 'Could not find the vehicle that was searched for',
  statens_vegvesen_vehicle_data_not_available: 'Could not find the vehicle that was searched for',
  booking_proposal_closed: `The booking proposal is closed`,
  booking_proposal_closed_by_staff: `This proposal has been closed`,
  booking_proposal_rejected: `This proposal has been rejected`,
  booking_proposal_rejected_by_customer: `This proposal has been rejected`,
  booking_proposal_is_used_has_booking: `The booking proposal is already in use`,
  booking_proposal_expired: `This proposal has expired`,
  booking_proposal_not_published: `This proposal is not available`,
  booking_proposal_booked_outside_of_recall: `This proposal is no longer available`,
  request_throttled: `You've tried too many times, so we've temporarily blocked your request. If you need help, please contact support`,
  not_found: `The content you are looking for was not found, please contact support if this is unexpected`,
  throttled:
    "You've tried too many times, so we've temporarily blocked your request. If you need help, please contact support",
  tire_quote_already_has_selected_item: 'You have already accepted this tire offer',
  login_error: "We couldn't log you in. Please try again.",
  sms_verification_rate_limit: 'Too many verification attempts. Please wait a few minutes and try again.',
  sms_pumping_protection_blocked: 'This phone number is temporarily blocked. Please contact support.',
  phone_number_country_code_blocked: "Phone numbers from this country aren't supported.",
  phone_number_cannot_receive_sms: "This number can't receive SMS. Use a mobile number.",
  user_does_not_exist: 'No account found for that email or phone number.',
  coupon_error: "We couldn't apply that discount. Please try again.",
  coupon_not_found: "That discount code isn't valid.",
  car_missing_car_data: 'Some required vehicle details are missing. Please contact support.',
  booking_already_invoiced: "This booking has already been invoiced and can't be modified.",
  booking_creation_failed: "We couldn't create the booking. Please try again.",
  recall_proposal_no_valid_delivery_windows: 'No available time slots for your location.',
  delivery_windows_not_found_for_pickup_bookings: 'No pickup times are available. Please try again later.',
  tire_quote_already_accepted: "You've already accepted this tire offer.",
  order_not_found: 'Order not found.',
  order_already_paid: 'This order has already been paid.',
  order_is_not_paid: "This order hasn't been paid yet.",
  double_payment: 'Payment has already been processed for this order.',
  failed_to_create_payment_session: "We couldn't start the payment session. Please try again.",
  order_already_refunded: 'This order has already been refunded.',
  wheel_storage_pickup_power_of_attorney_already_set_error: 'Power of attorney is already set for this pickup.',
  incomplete_address:
    'The selected address does not contain enough information. Please select a more specific address.',
  address_not_close_to_service_department: 'This address is outside our service area.',
  service_department_not_found_api_error:
    'This address is outside the service area for the selected products, so no time slots are shown.',
  country_not_supported: "Our service isn't available in this country.",
  already_member: "You're already a member.",
  error_sending_verification: "We couldn't send the verification code. Please try again.",
  error_checking_verification: "We couldn't verify the code. Please try again.",
  signed_document_not_found: 'Signed document not found.',
  signed_wheel_pickup_document_not_found: 'Signed wheel pickup document not found.',
  twilio_authentication_error: 'Our SMS provider is experiencing issues. Please try again later.',
  twilio_phone_fraud_blocked:
    "Our SMS provider has temporarily blocked this phone number. We're working on a fix, but it may take a few hours or up to a day. We're sorry for the inconvenience. Please try again later.",
  booking_exists_for_season:
    'A booking already exists for this car and season. You can view or manage it through the link below.',
  wheel_storage_conflict:
    'Another customer has wheels in storage for the car with license plate, sign into a different account or contact support for help.',
  google_places_regular_opening_hours_unavailable:
    'Google has no regular weekly opening hours for this listing. Set hours manually or check the Google Business profile / place link.',
  opening_hours_not_configured:
    'Opening hours must be saved for this location before you can sync public holidays. Add a weekly schedule first.',
  nager_date_public_holidays_unavailable:
    "We couldn't reach the public holidays service (Nager.Date). Try again later, or add holiday exceptions manually.",
  transfer_dispatch_storage_facility_missing_reload_node: 'This storage facility has no stop timing configured.',
  transfer_dispatch_storage_facility_missing_stop_timing: 'This storage facility has no stop timing configured.',
  transfer_dispatch_stop_timing_missing_wheel_cargo_config:
    'This storage facility is missing wheel load/unload timings.',
  // Booking sales-item availability — expected customer selection outcomes (wrong
  // address/brand/car combination). UI shows a friendly empty state; do not
  // treat as operational failures.
  no_sales_items_for_brands_at_address: 'No products for the selected brand(s) at this address.',
  no_sales_items_at_address: 'No products available at this address.',
  no_sales_items_for_car_rules: 'No products match the selected vehicle rules.',
  no_sales_items_upcoming_wheel_storage_booking:
    'Vehicle already has an upcoming tire hotel booking, so no products are available.',
  no_sales_items_active_wheel_storage:
    'Vehicle has active wheel storage that blocks available products for this selection.',
  no_sales_items_wheel_storage_catalog_state:
    'Wheel storage catalog state blocks available products for this selection.',
  no_sales_items_available_for_car: 'No products available for this vehicle.',
  no_service_departments_available_for_bookings: 'No service departments available for bookings at this address.',
  campaign_departments_required: 'Pick at least one department, or make the campaign organization-wide.',
  campaign_departments_invalid: 'Organization-wide campaigns cannot list specific departments.',
  campaign_department_not_in_organization: 'A selected department is not in this organization.',
  organization_registration_number_already_set_error: 'This organization already has a registration number.',
  campaign_auth_code_invalid: 'The discount code is not valid. Please check the code and try again.',
  campaign_brand_mismatch:
    'This campaign belongs to a different brand. Open the link from the brand that sent you this offer.',
  campaign_expired: 'This campaign has expired and can no longer be claimed.',
  campaign_no_longer_available: 'This campaign is no longer available.',
  campaign_requires_naf_verification:
    'This campaign requires NAF membership verification before you can activate the benefits.',
  referral_brand_mismatch:
    'This referral belongs to a different brand. Open the link from the brand that sent you this offer.',
  referral_disabled: 'Referrals are not available.',
  referral_no_longer_available: 'This referral link is no longer available.',
  referral_settings_not_found: 'Referral program not configured for this brand.'
} as const;
